import { keepPreviousData, useQuery } from '@tanstack/react-query'

import { apiClient } from 'api/apiClient/apiClient'
import { ELPLAN_BID_REFRESH_EVENT_QUERY_KEY } from 'api/elplan/elplan.constants'
import { OptJobType } from 'api/optJobs/optJobs.api'
import { calculateDatasetsWithSameReturnId, getCalculatedDataset } from 'helpers/dataset.helper/dataset.calc.helper'
import { getDatasetInstructionsInExecutionOrder } from 'helpers/dataset.helper/dataset.calcDependencies.helper'
import {
  applyZoomToDatasets,
  ensureUniformTimeIntervals,
  fillPeriodWithFirstIndexValue,
  getEarliestStartTime,
  getLatestEndTime,
  mergeDatasetsWithSameReturnId,
  setSmallValuesToZero,
} from 'helpers/dataset.helper/dataset.helper'
import { clone } from 'helpers/global.helper/global.helper'
import { queryClient } from 'helpers/queryClient'
import { useMemo } from 'react'
import uiConfigStore from 'store/uiConfig/uiConfig'
import zoomStore from 'store/zoom/zoom'
import Datetime from 'utils/datetime/datetime'
import { fetchDatasetsFromDbS } from 'utils/dbs/dbs'
import getLogger from 'utils/log/log'
import getQueue from 'utils/queue/queue'
import sentry from 'utils/sentry/sentry'
import { snapshot, useSnapshot } from 'valtio'
import optimizeViewStore from 'views/OptimizeView/store/optimizeViewStore'
import { makeValueNegative, negateValue, postProcessDatasets } from './dataset.api.helper'
import {
  DATASET_FOR_UI_CONFIG,
  DATASET_FOR_UI_CONFIG_REFRESH_ON_FUEL_PLAN,
  DATASET_FOR_UI_CONFIG_REFRESH_ON_OPTIMIZE,
  DATASET_FOR_UI_CONFIG_REFRESH_ON_OPTIMIZE_WIP,
  DATASET_INSTRUCTION,
  KEEP_PREVIOUS_DATA_QUERY_KEYS,
} from './dataset.constants'

// Re-exported for backwards compatibility — the canonical home is dataset.constants.ts.
export * from './dataset.constants'

const log = getLogger('dataset.api')

export function datasetResToDatasets(datasetRes: DatasetResponse, datasetInstruction: DatasetInstruction): Dataset[] {
  // Dummy dataset instruction if none is provided that will apply no filters
  if (!datasetInstruction) {
    datasetInstruction = {
      type: 'dbs',
      contract: {
        metadata_filter: {
          include: {},
          exclude: {},
        },
      },
    }
  }

  const times = datasetRes.data.map((d) => Datetime.toISOString(d.time))
  const returnIdToValues = datasetRes.data.reduce(
    (acc, d) => {
      Object.entries(d)
        .filter(([k]) => k !== `time`)
        .forEach(([returnId, value]) => {
          if (!acc[returnId]) {
            acc[returnId] = []
          }

          acc[returnId].push(value as number | null)
        })

      return acc
    },
    {} as Record<string, (number | null)[]>
  )

  const datasets: Dataset[] = Object.entries(returnIdToValues).map(([return_id, values]) => {
    return {
      return_id,
      times: times.slice(0, values.length),
      values,
      status: datasetRes.status,
      error_msg: datasetRes.error_msg,
    }
  })

  return postProcessDatasets(datasets, datasetInstruction)
}

const fetchDataset = async ({
  datasetInstruction,
  backendDatasetInstruction,
}: {
  datasetInstruction: Partial<DatasetInstruction>
  backendDatasetInstruction: Partial<DatasetInstruction>
}): Promise<Dataset[]> => {
  const datasetRes = await apiClient<DatasetResponse>(`dataset/${datasetInstruction.type}`, {
    method: `POST`,
    data: backendDatasetInstruction,
  })

  if (!datasetRes) {
    throw new Error('No dataset response received from server')
  }

  return datasetResToDatasets(datasetRes, datasetInstruction as DatasetInstruction)
}

export async function fetchDatasetsFromDjango(
  datasetInstructions: Partial<DatasetInstruction>[],
  useQueue?: boolean
): Promise<Dataset[]> {
  const queue = getQueue()

  return (
    await Promise.all(
      datasetInstructions
        .filter(({ type }) => type !== `calc` && type !== 'dbs' && type !== 'template')
        .map(async (datasetInstruction) => {
          const errorDataset: Dataset = {
            return_id: datasetInstruction.return_id ?? '',
            values: [],
            times: [],
          }

          try {
            const backendDatasetInstruction = clone(datasetInstruction)
            if (backendDatasetInstruction.filter?.aggregate_in_frontend) {
              delete backendDatasetInstruction.filter.aggregate
            }

            if (backendDatasetInstruction.filter) {
              if (!backendDatasetInstruction.filter.start_time) {
                delete backendDatasetInstruction.filter.start_time
              }
              if (!backendDatasetInstruction.filter.end_time) {
                delete backendDatasetInstruction.filter.end_time
              }
            }
            if (useQueue !== false) {
              return queue(async () => {
                try {
                  return await fetchDataset({
                    datasetInstruction: datasetInstruction,
                    backendDatasetInstruction: backendDatasetInstruction,
                  })
                } catch (error: unknown) {
                  sentry.captureException(error as Error, {
                    level: 'warning',
                    tags: {
                      sentry_scope: 'dataset_fetch',
                      issue_type: 'django_dataset_fetch_failure',
                      dataset_type: datasetInstruction.type ?? 'unknown',
                    },
                    extra: {
                      returnId: datasetInstruction.return_id,
                      useQueue,
                    },
                    fingerprint: [
                      'dataset_fetch',
                      'django_dataset_fetch_failure',
                      datasetInstruction.type ?? 'unknown',
                    ],
                  })
                  errorDataset.status = `error`
                  errorDataset.error_msg = (error as Error)?.message ?? ``
                  return [errorDataset]
                }
              })
            } else {
              return await fetchDataset({
                datasetInstruction: datasetInstruction,
                backendDatasetInstruction: backendDatasetInstruction,
              })
            }
          } catch (error: unknown) {
            sentry.captureException(error as Error, {
              level: 'warning',
              tags: {
                sentry_scope: 'dataset_fetch',
                issue_type: 'django_dataset_fetch_failure',
                dataset_type: datasetInstruction.type ?? 'unknown',
              },
              extra: {
                returnId: datasetInstruction.return_id,
                useQueue,
              },
              fingerprint: ['dataset_fetch', 'django_dataset_fetch_failure', datasetInstruction.type ?? 'unknown'],
            })
            errorDataset.status = `error`
            errorDataset.error_msg = (error as Error)?.message ?? ``
          }

          return [errorDataset]
        })
    )
  ).flat()
}

export async function getDatasets(
  id: any,
  version: number | undefined,
  _datasetInstructions: any,
  datasetStartTime?: ISODateTime,
  datasetEndTime?: ISODateTime,
  options: {
    useQueue?: boolean
    fillMissingHourTimestamps?: boolean // This is kept for backward compatibility but ensureUniformTimeIntervals should be used going forward
    ensureUniformTimeIntervals?: boolean
    overrideAlias?: UiConfigAliases
    onlyReturnIds?: string[]
    returnIdRegexFilter?: string
    datasetCreatedAt?: string
    datasetCreatedAtOperator?: DatasetCreatedAtOperators
    forceFillMissingHours?: boolean
  } = {
    useQueue: true,
    fillMissingHourTimestamps: true,
    ensureUniformTimeIntervals: true,
    overrideAlias: {},
    onlyReturnIds: [],
    returnIdRegexFilter: '.*',
  }
): Promise<Dataset[]> {
  if (!options?.overrideAlias) {
    options.overrideAlias = {}
  }

  if (!_datasetInstructions?.length) {
    return []
  }

  // Serialize dataset instructions
  let datasetInstructions = clone(_datasetInstructions)
  if (datasetStartTime) {
    datasetInstructions = datasetInstructions.map((instruction: any) => ({
      ...instruction,
      filter: {
        ...instruction.filter,
        offset_start_time: 0,
        start_time: Datetime.toISOString(datasetStartTime),
      },
    }))

    options.overrideAlias.start_time = Datetime.toISOString(datasetStartTime)
    options.overrideAlias.offset_start_time = 0
  }

  if (datasetEndTime) {
    datasetInstructions = datasetInstructions.map((instruction: any) => ({
      ...instruction,
      filter: {
        ...instruction.filter,
        offset_end_time: 0,
        end_time: Datetime.toISOString(datasetEndTime),
      },
    }))

    options.overrideAlias.end_time = Datetime.toISOString(datasetEndTime)
    options.overrideAlias.offset_end_time = 0
  }

  if (options.datasetCreatedAt) {
    datasetInstructions = datasetInstructions.map((instruction: any) => ({
      ...instruction,
      filter: {
        ...instruction.filter,
        created_at: options.datasetCreatedAt,
        created_at_operator: options.datasetCreatedAtOperator,
      },
    }))
  }

  let datasets: Dataset[] = []
  const uiConfigSnap = snapshot(uiConfigStore)
  const uid = uiConfigSnap.idToUiConfig[id]?.uid
  const uiConfig = uid ? uiConfigSnap.getParsedUiConfig(uid, options.overrideAlias) : null

  const hasDbSInstructions = datasetInstructions.some((di: DatasetInstruction) => di.type === 'dbs')
  if (hasDbSInstructions) {
    datasets = await fetchDatasetsFromDbS(datasetInstructions, {
      onlyReturnIds: options.onlyReturnIds,
      returnIdRegexFilter: options.returnIdRegexFilter,
    })
  }

  if (datasetInstructions.some((di: any) => di.type === 'template')) {
    log.warn("Attempting to fetch 'template' dataset from django.", {
      uiConfigId: id,
      uiConfigVersion: version,
      system: uiConfig?.system,
    })
    sentry.captureScopedMessage({
      scope: 'dataset_fetch',
      issueType: 'template_dataset_from_django',
      message: "Attempting to fetch 'template' dataset from django",
      extra: {
        uiConfigId: id,
        uiConfigVersion: version,
        system: uiConfig?.system,
        datasetInstructionCount: datasetInstructions.length,
      },
    })
  }

  // Fetch datasets from Django
  const djangoFetchedDatasets = fetchDatasetsFromDjango(datasetInstructions, options.useQueue)

  datasets.push(...(await djangoFetchedDatasets))

  // Post-process datasets & calculate derived datasets
  if (uiConfig?.props.operator_for_same_data_id) {
    // Calculate datasets with same return_id, depending on the operator
    datasets = calculateDatasetsWithSameReturnId(datasets, uiConfig?.props.operator_for_same_data_id as string)
  } else {
    // Merge datasets with same return_id
    datasets = mergeDatasetsWithSameReturnId(datasets)
  }

  // Ensure uniform time intervals if option is set
  if (options?.fillMissingHourTimestamps || options?.ensureUniformTimeIntervals) {
    const earliestStartTime = getEarliestStartTime(datasetInstructions)
    const latestEndTime = getLatestEndTime(datasetInstructions)

    if (Datetime.isBefore(earliestStartTime, latestEndTime)) {
      datasets = ensureUniformTimeIntervals(datasets, earliestStartTime, latestEndTime, options?.forceFillMissingHours)
    } else {
      sentry.captureException(
        new Error(
          `Earliest start time (${earliestStartTime}) is after latest end time (${latestEndTime}) for ui config ${id} version ${version}`
        ),
        {
          level: 'warning',
          tags: {
            sentry_scope: 'dataset_fetch',
            issue_type: 'invalid_time_interval',
          },
          extra: {
            uiConfigId: id,
            uiConfigVersion: version,
            earliestStartTime,
            latestEndTime,
          },
          fingerprint: ['dataset_fetch', 'invalid_time_interval'],
        }
      )
    }
  }

  return datasets
}

export function convertDatasetInstructionsToQueryKey(
  datasetInstrucitons: Partial<DatasetInstruction>[]
): Partial<DatasetInstruction>[] {
  // Remove irrelevant data from dataset instruction that the server does not need. This will cache similar queries even if they are not identical.
  let di = clone(datasetInstrucitons)
  di = di.map((d) => {
    if (d?.filter?.aggregate_in_frontend) {
      d.filter.aggregate = ''
    }

    return d
  })

  return di
}

export function getSandboxProjectUiConfigRefreshToken(sandboxProjectId: number) {
  return `sandboxProjectUiConfigRefreshToken_${sandboxProjectId}`
}

export function useDatasets(
  id: number,
  version: number,
  _datasetInstructions: Partial<DatasetInstruction>[],
  datasetStartTime?: ISODateTime,
  datasetEndTime?: ISODateTime,
  options: {
    ignoreZoom?: boolean
    uid?: number
    fillMissingHourTimestamps?: boolean // This is kept for backward compatibility but ensureUniformTimeIntervals should be used going forward
    ensureUniformTimeIntervals?: boolean
    useQueue?: boolean
    refreshOnOptimize?: boolean
    datasetRefreshToken?: string
    overrideAlias?: UiConfigAliases
    onlyReturnIds?: string[]
    returnIdRegexFilter?: string
    datasetCreatedAt?: string
    datasetCreatedAtOperator?: DatasetCreatedAtOperators
    forceFillMissingHours?: boolean
    isFiltered?: boolean
    keepPreviousData?: boolean
  } = {
    ignoreZoom: false,
    fillMissingHourTimestamps: true,
    ensureUniformTimeIntervals: true,
    datasetRefreshToken: DATASET_FOR_UI_CONFIG,
    overrideAlias: {},
    onlyReturnIds: [],
    returnIdRegexFilter: '.*',
  }
) {
  /*
  1. Fetch & cache API-response for datasets for entire period selected.
  2. Apply zoom-state for path route.
  3. Calc datasets after zoom-state.
*/

  const queryRefreshKey = options?.datasetRefreshToken || DATASET_FOR_UI_CONFIG
  const shouldKeepPreviousData = options.keepPreviousData ?? KEEP_PREVIOUS_DATA_QUERY_KEYS.has(queryRefreshKey)
  const zoomSnap = useSnapshot(zoomStore)
  const zoomStartTime = zoomSnap.startTime
  const zoomEndTime = zoomSnap.endTime

  // Cached API-call
  const datasetResponse = useQuery({
    queryKey: [
      queryRefreshKey,
      convertDatasetInstructionsToQueryKey(_datasetInstructions),
      datasetStartTime,
      datasetEndTime,
      {
        onlyReturnIds: options.onlyReturnIds,
        returnIdRegexFilter: options.returnIdRegexFilter,
        datasetCreatedAt: options.datasetCreatedAt,
        datasetCreatedAtOperator: options.datasetCreatedAtOperator,
        overrideAlias: options.overrideAlias,
      }, // Only these options affect the query key; other options may still be sent in the request
    ],

    queryFn: async () => {
      const fillMissingHourTimestamps = options?.fillMissingHourTimestamps !== false
      const ensureUniformTimeIntervals = options?.ensureUniformTimeIntervals !== false
      const useQueue = options?.useQueue !== false

      return getDatasets(id, version, _datasetInstructions, datasetStartTime, datasetEndTime, {
        fillMissingHourTimestamps,
        ensureUniformTimeIntervals,
        useQueue,
        overrideAlias: options.overrideAlias,
        onlyReturnIds: options.onlyReturnIds,
        returnIdRegexFilter: options.returnIdRegexFilter,
        datasetCreatedAt: options.datasetCreatedAt,
        datasetCreatedAtOperator: options.datasetCreatedAtOperator,
        forceFillMissingHours: options.forceFillMissingHours,
      })
    },
    placeholderData: shouldKeepPreviousData ? keepPreviousData : undefined,
  })

  //only calc datasets when there is data or if zoomstate has changed
  const calculatedDatasets = useMemo(() => {
    let datasets: any = datasetResponse.data ?? []
    if (datasetResponse.status !== 'success') {
      return []
    }

    datasets = options.ignoreZoom
      ? clone(datasets || [])
      : clone(applyZoomToDatasets(datasets || [], zoomStartTime, zoomEndTime))

    // Calculation datasets (based on fetched datasets)
    return calculateCalcDatasets(_datasetInstructions as DatasetInstruction[], datasets, options.isFiltered)
  }, [
    _datasetInstructions,
    datasetResponse.data,
    datasetResponse.status,
    options.ignoreZoom,
    options.isFiltered,
    zoomEndTime,
    zoomStartTime,
  ])

  // Instead of mutating datasetResponse, return a new object
  return {
    ...datasetResponse,
    data: calculatedDatasets.length ? calculatedDatasets : datasetResponse.data,
  }
}

export function calculateCalcDatasets(datasetInstructions: any[], datasets: any[], isFiltered?: boolean): any[] {
  const datasetReturnIds = datasets.map((d) => d.return_id)
  const calcDatasetInstructions = getDatasetInstructionsInExecutionOrder(datasetInstructions, datasetReturnIds, {
    isFiltered,
  })
  calcDatasetInstructions.forEach((datasetInstruction) => {
    //TOOD: all these filter-functions also exists in function postProcessDatasets(), perhaps we should use the same functions for calc-datasets as well?

    let dataset = getCalculatedDataset(clone(datasets), datasetInstruction)

    if (datasetInstruction.filter?.fill_period_with_first_index_value) {
      dataset = fillPeriodWithFirstIndexValue(datasetInstruction, dataset)
    }

    if (datasetInstruction.filter?.zero_is_nan) {
      const zerosAreNan = dataset.values.map((v) => (v === 0 ? null : v))
      dataset.values = zerosAreNan
    }
    if (datasetInstruction.filter?.show_as_negative) {
      const negativeValues = dataset.values.map((v) => (v !== null ? makeValueNegative(v) : v))
      dataset.values = negativeValues
    }

    if (datasetInstruction.filter?.negate_values) {
      const oppositeValues = dataset.values.map((v) => (v !== null ? negateValue(v) : v))
      dataset.values = oppositeValues
    }

    datasets.push(dataset)
  })

  datasets = setSmallValuesToZero(datasets, 0.0001)

  return datasets
}

export function updateOptimizeHourRange() {
  optimizeViewStore.updateCurrentOptimizationHour()
  optimizeViewStore.updateOptimizedViewRange()
}

export function reFetchAllDatasetsOnOptimize(type?: OptJobType) {
  updateOptimizeHourRange()

  if (type === 'regular_wip') {
    queryClient.invalidateQueries({ queryKey: [DATASET_FOR_UI_CONFIG_REFRESH_ON_OPTIMIZE_WIP] })
  } else {
    queryClient.invalidateQueries({ queryKey: [DATASET_FOR_UI_CONFIG_REFRESH_ON_OPTIMIZE] })
  }
}

// TODO: We may have a case in the future or soon, that this has to be re-feteched every hour.
export function reFetchAllDatasetsOnFuelPlan() {
  queryClient.invalidateQueries({ queryKey: [DATASET_FOR_UI_CONFIG_REFRESH_ON_FUEL_PLAN] })
}

export function reFetchAllDatasetsOnElectricityPriceScenario() {
  queryClient.invalidateQueries({ queryKey: [ELPLAN_BID_REFRESH_EVENT_QUERY_KEY] })
}

export function useDatasetInstructions(systemId: number, datasetTypes: string[], measDataForDisplay = false) {
  return useQuery({
    queryKey: [DATASET_INSTRUCTION, systemId, datasetTypes],

    queryFn: async () => {
      if (datasetTypes.length === 0) {
        return []
      }
      const typesQuery = datasetTypes.map((type) => `${type}`).join(`&type=`)

      const data = apiClient<DatasetInstruction[]>('dataset/dataset_instructions', {
        method: 'GET',
        params: {
          system_id: systemId,
          meas_data_for_display: measDataForDisplay,
          type: typesQuery,
        },
      })

      if (!data) {
        return []
      }
      return data
    },
  })
}
